import { Observable } from 'graphql-typed-client'

export interface Query {
  countries: Country[]
  currencies: Currency[]
  currentUser: User | null
  products: Product[]
  standards: Standards[]
  resetPasswordTokenValid: Boolean
  ping: String
  __typename: 'Query'
}

/** An enumeration. */
export enum UserLanguages {
  en = 'en',
  zh = 'zh',
  fr = 'fr',
  pt = 'pt',
  es = 'es',
  ja = 'ja',
}

export interface Country {
  name: String
  code: String
  __typename: 'Country'
}

/** The `String` scalar type represents textual data, represented as UTF-8 character sequences. The String type is most often used by GraphQL to represent free-form human-readable text. */
export type String = string

export interface Currency {
  id: ID
  name: String
  code: String
  sign: String
  conversionRate: Float
  conversionRateDate: DateTime
  sortOrder: Int
  calculationinputSet: CalculationInputObj[]
  standardsregionSet: StandardsRegion[]
  __typename: 'Currency'
}

/** The `ID` scalar type represents a unique identifier, often used to refetch an object or as key for a cache. The ID type appears in a JSON response as a String; however, it is not intended to be human-readable. When expected as an input type, any string (such as `"4"`) or integer (such as `4`) input value will be accepted as an ID. */
export type ID = string

/** The `Float` scalar type represents signed double-precision fractional values as specified by [IEEE 754](https://en.wikipedia.org/wiki/IEEE_floating_point). */
export type Float = number

/**
 * The `DateTime` scalar type represents a DateTime
 * value as specified by
 * [iso8601](https://en.wikipedia.org/wiki/ISO_8601).
 */
export type DateTime = any

/** The `Int` scalar type represents non-fractional signed whole numeric values. Int can represent values between -(2^31) and 2^31 - 1. */
export type Int = number

export interface CalculationInputObj {
  id: ID
  units: MainCalculationInputUnitsChoices
  resultsUnitsAmount: MainCalculationInputResultsUnitsAmountChoices
  resultsUnitsPeriod: MainCalculationInputResultsUnitsPeriodChoices
  currency: Currency
  product: Product
  productPrice: Float
  region: StandardsRegion
  herdSize: Int
  aabCents: Int
  isPlusDmi: Boolean
  isAab: Boolean
  investmentFarOffStatus: MainCalculationInputInvestmentFarOffStatusChoices
  investmentCloseUpStatus: MainCalculationInputInvestmentCloseUpStatusChoices
  investmentFreshStatus: MainCalculationInputInvestmentFreshStatusChoices
  investmentEarlyMidLactStatus: MainCalculationInputInvestmentEarlyMidLactStatusChoices
  investmentLateLactStatus: MainCalculationInputInvestmentLateLactStatusChoices
  investmentHerdStructureFarOff: Float
  investmentHerdStructureCloseUp: Float
  investmentHerdStructureFresh: Float
  investmentHerdStructureEarlyMidLact: Float
  investmentHerdStructureLateLact: Float
  investmentProductGramsPerCowDayCloseUp: Float
  investmentProductGramsPerCowDayFresh: Float
  investmentProductGramsPerCowDayEarlyMidLact: Float
  investmentProductGramsPerCowDayLateLact: Float
  investmentDmiFarOff: Float
  investmentDmiCloseUp: Float
  investmentDmiFresh: Float
  investmentDmiEarlyMidLact: Float
  investmentDmiLateLact: Float
  investmentDmCostCurrentFarOff: Float
  investmentDmCostCurrentCloseUp: Float
  investmentDmCostCurrentFresh: Float
  investmentDmCostCurrentEarlyMidLact: Float
  investmentDmCostCurrentLateLact: Float
  shortTermReturnsMilkCurrent: Float
  shortTermReturnsMilkDifference: Float
  shortTermReturnsMilkFmmoPrice: Float
  shortTermReturnsFatCurrent: Float
  shortTermReturnsFatDifference: Float
  shortTermReturnsFatFmmoPrice: Float
  shortTermReturnsProteinCurrent: Float
  shortTermReturnsProteinDifference: Float
  shortTermReturnsProteinFmmoPrice: Float
  shortTermReturnsOtherSolidsCurrent: Float
  shortTermReturnsOtherSolidsDifference: Float
  shortTermReturnsOtherSolidsFmmoPrice: Float
  shortTermReturnsPremiumFmmoPrice: Float
  longTermReturnsReproductionCostCurrent: Float
  longTermReturnsReplacementCostCurrent: Float
  longTermReturnsCullValueCurrent: Float
  longTermReturnsCalfValueCurrent: Float
  longTermReturnsLastMonthToBreedCurrent: Float
  longTermReturnsMinimumMilkToBreedCurrent: Float
  longTermReturnsCullingRateCurrent: Float
  longTermReturnsPregnancyRateCurrent: Float
  longTermReturnsPregnancyRateDifference: Float
  longTermReturnsPregnancyLossCurrent: Float
  longTermReturnsPregnancyLossDifference: Float
  longTermReturnsMastitisRegular: Float
  longTermReturnsMastitisReduced: Float
  longTermReturnsMastitisCostPerCase: Float
  longTermReturnsKetosisClinicalRegular: Float
  longTermReturnsKetosisClinicalReduced: Float
  longTermReturnsKetosisClinicalCostPerCase: Float
  longTermReturnsKetosisSubClinicalRegular: Float
  longTermReturnsKetosisSubClinicalReduced: Float
  longTermReturnsKetosisSubclinicalCostPerCase: Float
  longTermReturnsMetritisRegular: Float
  longTermReturnsMetritisReduced: Float
  longTermReturnsMetritisCostPerCase: Float
  calculationtemplate: CalculationTemplate | null
  __typename: 'CalculationInputObj'
}

/** An enumeration. */
export enum MainCalculationInputUnitsChoices {
  /** Metric */
  METRIC = 'METRIC',
  /** Imperial */
  IMPERIAL = 'IMPERIAL',
}

/** An enumeration. */
export enum MainCalculationInputResultsUnitsAmountChoices {
  /** Cow */
  COW = 'COW',
  /** Herd */
  HERD = 'HERD',
}

/** An enumeration. */
export enum MainCalculationInputResultsUnitsPeriodChoices {
  /** Day */
  DAY = 'DAY',
  /** Month */
  MONTH = 'MONTH',
  /** Year */
  YEAR = 'YEAR',
}

export interface Product {
  id: ID
  price: Float
  name: String
  metrics: ProductMetrics[]
  __typename: 'Product'
}

export interface ProductMetrics {
  standards: Standards
  economicPerformance: EconomicPerformance
  milkPerformance: MilkPerformance
  milkPerformanceDiff: MilkPerformanceDiff
  productFeedingRates: ProductFeedingRates
  reproductivePerformance: ReproductivePerformance
  diseasePerformances: DiseasePerformance[]
  __typename: 'ProductMetrics'
}

export interface Standards {
  id: ID
  region: StandardsRegion
  fmmoPricing: FmmoPricing
  herdComposition: ConstantsPerPhase
  dmi: ConstantsPerPhase
  currentFeedCosts: ConstantsPerPhase
  diseaseMetrics: DiseaseMetrics[]
  __typename: 'Standards'
}

export interface StandardsRegion {
  id: ID
  countries: Country[]
  currency: Currency
  forClient: MainStandardsRegionForClientChoices | null
  name: String
  units: MainStandardsRegionUnitsChoices
  calculationinputSet: CalculationInputObj[]
  standards: Standards | null
  __typename: 'StandardsRegion'
}

/** An enumeration. */
export enum MainStandardsRegionForClientChoices {
  /** Devenish */
  DEVENISH = 'DEVENISH',
}

/** An enumeration. */
export enum MainStandardsRegionUnitsChoices {
  /** Metric */
  METRIC = 'METRIC',
  /** Imperial */
  IMPERIAL = 'IMPERIAL',
}

export interface FmmoPricing {
  milk: Float
  fat: Float
  protein: Float
  otherSolids: Float
  premium: Float
  __typename: 'FmmoPricing'
}

export interface ConstantsPerPhase {
  farOff: Float
  closeUp: Float
  fresh: Float
  earlyMidLact: Float
  lateLact: Float
  __typename: 'ConstantsPerPhase'
}

export interface DiseaseMetrics {
  disease: DiseaseMetricsDisease
  usdCase: Float
  __typename: 'DiseaseMetrics'
}

/** An enumeration. */
export enum DiseaseMetricsDisease {
  MASTITIS_CLINICAL = 'MASTITIS_CLINICAL',
  METRITIS = 'METRITIS',
  KETOSIS_CLINICAL = 'KETOSIS_CLINICAL',
  KETOSIS_SUBCLINICAL = 'KETOSIS_SUBCLINICAL',
}

export interface EconomicPerformance {
  reproductionCost: Float
  replacementCost: Float
  cullValue: Float
  calfValue: Float
  cullingRate: Float
  __typename: 'EconomicPerformance'
}

export interface MilkPerformance {
  milk: Float
  fat: Float
  protein: Float
  otherSolids: Float
  __typename: 'MilkPerformance'
}

export interface MilkPerformanceDiff {
  milk: Float
  fat: Float
  protein: Float
  otherSolids: Float
  __typename: 'MilkPerformanceDiff'
}

export interface ProductFeedingRates {
  farOff: Float
  closeUp: Float
  fresh: Float
  earlyMidLact: Float
  lateLact: Float
  __typename: 'ProductFeedingRates'
}

export interface ReproductivePerformance {
  lastMonthToBreed: Float
  minimumMilkToBreed: Float
  pregnancyRate: Float
  pregnancyRateDiff: Float
  pregnancyLoss: Float
  pregnancyLossDiff: Float
  __typename: 'ReproductivePerformance'
}

export interface DiseasePerformance {
  disease: DiseasePerformanceDisease
  regularIncidence: Float
  reducedIncidence: Float
  reductionPercent: Float
  __typename: 'DiseasePerformance'
}

/** An enumeration. */
export enum DiseasePerformanceDisease {
  MASTITIS_CLINICAL = 'MASTITIS_CLINICAL',
  METRITIS = 'METRITIS',
  KETOSIS_CLINICAL = 'KETOSIS_CLINICAL',
  KETOSIS_SUBCLINICAL = 'KETOSIS_SUBCLINICAL',
}

/** The `Boolean` scalar type represents `true` or `false`. */
export type Boolean = boolean

/** An enumeration. */
export enum MainCalculationInputInvestmentFarOffStatusChoices {
  /** On */
  ON = 'ON',
  /** Off */
  OFF = 'OFF',
  /** Zeroed */
  ZEROED = 'ZEROED',
}

/** An enumeration. */
export enum MainCalculationInputInvestmentCloseUpStatusChoices {
  /** On */
  ON = 'ON',
  /** Off */
  OFF = 'OFF',
  /** Zeroed */
  ZEROED = 'ZEROED',
}

/** An enumeration. */
export enum MainCalculationInputInvestmentFreshStatusChoices {
  /** On */
  ON = 'ON',
  /** Off */
  OFF = 'OFF',
  /** Zeroed */
  ZEROED = 'ZEROED',
}

/** An enumeration. */
export enum MainCalculationInputInvestmentEarlyMidLactStatusChoices {
  /** On */
  ON = 'ON',
  /** Off */
  OFF = 'OFF',
  /** Zeroed */
  ZEROED = 'ZEROED',
}

/** An enumeration. */
export enum MainCalculationInputInvestmentLateLactStatusChoices {
  /** On */
  ON = 'ON',
  /** Off */
  OFF = 'OFF',
  /** Zeroed */
  ZEROED = 'ZEROED',
}

export interface CalculationTemplate {
  id: ID
  calculationInput: CalculationInputObj
  name: String
  __typename: 'CalculationTemplate'
}

export interface User {
  defaultCalculationTemplate: CalculationTemplate | null
  email: String
  language: UserLanguages
  country: MainUserCountryChoices
  clientCustomization: UserClientCustomizations | null
  calculationTemplates: CalculationTemplate[]
  __typename: 'User'
}

/** An enumeration. */
export enum MainUserCountryChoices {
  /** Afghanistan */
  AF = 'AF',
  /** Åland Islands */
  AX = 'AX',
  /** Albania */
  AL = 'AL',
  /** Algeria */
  DZ = 'DZ',
  /** American Samoa */
  AS = 'AS',
  /** Andorra */
  AD = 'AD',
  /** Angola */
  AO = 'AO',
  /** Anguilla */
  AI = 'AI',
  /** Antarctica */
  AQ = 'AQ',
  /** Antigua and Barbuda */
  AG = 'AG',
  /** Argentina */
  AR = 'AR',
  /** Armenia */
  AM = 'AM',
  /** Aruba */
  AW = 'AW',
  /** Australia */
  AU = 'AU',
  /** Austria */
  AT = 'AT',
  /** Azerbaijan */
  AZ = 'AZ',
  /** Bahamas */
  BS = 'BS',
  /** Bahrain */
  BH = 'BH',
  /** Bangladesh */
  BD = 'BD',
  /** Barbados */
  BB = 'BB',
  /** Belarus */
  BY = 'BY',
  /** Belgium */
  BE = 'BE',
  /** Belize */
  BZ = 'BZ',
  /** Benin */
  BJ = 'BJ',
  /** Bermuda */
  BM = 'BM',
  /** Bhutan */
  BT = 'BT',
  /** Bolivia */
  BO = 'BO',
  /** Bonaire, Sint Eustatius and Saba */
  BQ = 'BQ',
  /** Bosnia and Herzegovina */
  BA = 'BA',
  /** Botswana */
  BW = 'BW',
  /** Bouvet Island */
  BV = 'BV',
  /** Brazil */
  BR = 'BR',
  /** British Indian Ocean Territory */
  IO = 'IO',
  /** Brunei */
  BN = 'BN',
  /** Bulgaria */
  BG = 'BG',
  /** Burkina Faso */
  BF = 'BF',
  /** Burundi */
  BI = 'BI',
  /** Cabo Verde */
  CV = 'CV',
  /** Cambodia */
  KH = 'KH',
  /** Cameroon */
  CM = 'CM',
  /** Canada */
  CA = 'CA',
  /** Cayman Islands */
  KY = 'KY',
  /** Central African Republic */
  CF = 'CF',
  /** Chad */
  TD = 'TD',
  /** Chile */
  CL = 'CL',
  /** China */
  CN = 'CN',
  /** Christmas Island */
  CX = 'CX',
  /** Cocos (Keeling) Islands */
  CC = 'CC',
  /** Colombia */
  CO = 'CO',
  /** Comoros */
  KM = 'KM',
  /** Congo */
  CG = 'CG',
  /** Congo (the Democratic Republic of the) */
  CD = 'CD',
  /** Cook Islands */
  CK = 'CK',
  /** Costa Rica */
  CR = 'CR',
  /** Côte d'Ivoire */
  CI = 'CI',
  /** Croatia */
  HR = 'HR',
  /** Cuba */
  CU = 'CU',
  /** Curaçao */
  CW = 'CW',
  /** Cyprus */
  CY = 'CY',
  /** Czechia */
  CZ = 'CZ',
  /** Denmark */
  DK = 'DK',
  /** Djibouti */
  DJ = 'DJ',
  /** Dominica */
  DM = 'DM',
  /** Dominican Republic */
  DO = 'DO',
  /** Ecuador */
  EC = 'EC',
  /** Egypt */
  EG = 'EG',
  /** El Salvador */
  SV = 'SV',
  /** Equatorial Guinea */
  GQ = 'GQ',
  /** Eritrea */
  ER = 'ER',
  /** Estonia */
  EE = 'EE',
  /** Eswatini */
  SZ = 'SZ',
  /** Ethiopia */
  ET = 'ET',
  /** Falkland Islands (Malvinas) */
  FK = 'FK',
  /** Faroe Islands */
  FO = 'FO',
  /** Fiji */
  FJ = 'FJ',
  /** Finland */
  FI = 'FI',
  /** France */
  FR = 'FR',
  /** French Guiana */
  GF = 'GF',
  /** French Polynesia */
  PF = 'PF',
  /** French Southern Territories */
  TF = 'TF',
  /** Gabon */
  GA = 'GA',
  /** Gambia */
  GM = 'GM',
  /** Georgia */
  GE = 'GE',
  /** Germany */
  DE = 'DE',
  /** Ghana */
  GH = 'GH',
  /** Gibraltar */
  GI = 'GI',
  /** Greece */
  GR = 'GR',
  /** Greenland */
  GL = 'GL',
  /** Grenada */
  GD = 'GD',
  /** Guadeloupe */
  GP = 'GP',
  /** Guam */
  GU = 'GU',
  /** Guatemala */
  GT = 'GT',
  /** Guernsey */
  GG = 'GG',
  /** Guinea */
  GN = 'GN',
  /** Guinea-Bissau */
  GW = 'GW',
  /** Guyana */
  GY = 'GY',
  /** Haiti */
  HT = 'HT',
  /** Heard Island and McDonald Islands */
  HM = 'HM',
  /** Holy See */
  VA = 'VA',
  /** Honduras */
  HN = 'HN',
  /** Hong Kong */
  HK = 'HK',
  /** Hungary */
  HU = 'HU',
  /** Iceland */
  IS = 'IS',
  /** India */
  IN = 'IN',
  /** Indonesia */
  ID = 'ID',
  /** Iran */
  IR = 'IR',
  /** Iraq */
  IQ = 'IQ',
  /** Ireland */
  IE = 'IE',
  /** Isle of Man */
  IM = 'IM',
  /** Israel */
  IL = 'IL',
  /** Italy */
  IT = 'IT',
  /** Jamaica */
  JM = 'JM',
  /** Japan */
  JP = 'JP',
  /** Jersey */
  JE = 'JE',
  /** Jordan */
  JO = 'JO',
  /** Kazakhstan */
  KZ = 'KZ',
  /** Kenya */
  KE = 'KE',
  /** Kiribati */
  KI = 'KI',
  /** Kuwait */
  KW = 'KW',
  /** Kyrgyzstan */
  KG = 'KG',
  /** Laos */
  LA = 'LA',
  /** Latvia */
  LV = 'LV',
  /** Lebanon */
  LB = 'LB',
  /** Lesotho */
  LS = 'LS',
  /** Liberia */
  LR = 'LR',
  /** Libya */
  LY = 'LY',
  /** Liechtenstein */
  LI = 'LI',
  /** Lithuania */
  LT = 'LT',
  /** Luxembourg */
  LU = 'LU',
  /** Macao */
  MO = 'MO',
  /** Madagascar */
  MG = 'MG',
  /** Malawi */
  MW = 'MW',
  /** Malaysia */
  MY = 'MY',
  /** Maldives */
  MV = 'MV',
  /** Mali */
  ML = 'ML',
  /** Malta */
  MT = 'MT',
  /** Marshall Islands */
  MH = 'MH',
  /** Martinique */
  MQ = 'MQ',
  /** Mauritania */
  MR = 'MR',
  /** Mauritius */
  MU = 'MU',
  /** Mayotte */
  YT = 'YT',
  /** Mexico */
  MX = 'MX',
  /** Micronesia */
  FM = 'FM',
  /** Moldova */
  MD = 'MD',
  /** Monaco */
  MC = 'MC',
  /** Mongolia */
  MN = 'MN',
  /** Montenegro */
  ME = 'ME',
  /** Montserrat */
  MS = 'MS',
  /** Morocco */
  MA = 'MA',
  /** Mozambique */
  MZ = 'MZ',
  /** Myanmar */
  MM = 'MM',
  /** Namibia */
  NA = 'NA',
  /** Nauru */
  NR = 'NR',
  /** Nepal */
  NP = 'NP',
  /** Netherlands */
  NL = 'NL',
  /** New Caledonia */
  NC = 'NC',
  /** New Zealand */
  NZ = 'NZ',
  /** Nicaragua */
  NI = 'NI',
  /** Niger */
  NE = 'NE',
  /** Nigeria */
  NG = 'NG',
  /** Niue */
  NU = 'NU',
  /** Norfolk Island */
  NF = 'NF',
  /** North Korea */
  KP = 'KP',
  /** North Macedonia */
  MK = 'MK',
  /** Northern Mariana Islands */
  MP = 'MP',
  /** Norway */
  NO = 'NO',
  /** Oman */
  OM = 'OM',
  /** Pakistan */
  PK = 'PK',
  /** Palau */
  PW = 'PW',
  /** Palestine, State of */
  PS = 'PS',
  /** Panama */
  PA = 'PA',
  /** Papua New Guinea */
  PG = 'PG',
  /** Paraguay */
  PY = 'PY',
  /** Peru */
  PE = 'PE',
  /** Philippines */
  PH = 'PH',
  /** Pitcairn */
  PN = 'PN',
  /** Poland */
  PL = 'PL',
  /** Portugal */
  PT = 'PT',
  /** Puerto Rico */
  PR = 'PR',
  /** Qatar */
  QA = 'QA',
  /** Réunion */
  RE = 'RE',
  /** Romania */
  RO = 'RO',
  /** Russia */
  RU = 'RU',
  /** Rwanda */
  RW = 'RW',
  /** Saint Barthélemy */
  BL = 'BL',
  /** Saint Helena, Ascension and Tristan da Cunha */
  SH = 'SH',
  /** Saint Kitts and Nevis */
  KN = 'KN',
  /** Saint Lucia */
  LC = 'LC',
  /** Saint Martin (French part) */
  MF = 'MF',
  /** Saint Pierre and Miquelon */
  PM = 'PM',
  /** Saint Vincent and the Grenadines */
  VC = 'VC',
  /** Samoa */
  WS = 'WS',
  /** San Marino */
  SM = 'SM',
  /** Sao Tome and Principe */
  ST = 'ST',
  /** Saudi Arabia */
  SA = 'SA',
  /** Senegal */
  SN = 'SN',
  /** Serbia */
  RS = 'RS',
  /** Seychelles */
  SC = 'SC',
  /** Sierra Leone */
  SL = 'SL',
  /** Singapore */
  SG = 'SG',
  /** Sint Maarten (Dutch part) */
  SX = 'SX',
  /** Slovakia */
  SK = 'SK',
  /** Slovenia */
  SI = 'SI',
  /** Solomon Islands */
  SB = 'SB',
  /** Somalia */
  SO = 'SO',
  /** South Africa */
  ZA = 'ZA',
  /** South Georgia and the South Sandwich Islands */
  GS = 'GS',
  /** South Korea */
  KR = 'KR',
  /** South Sudan */
  SS = 'SS',
  /** Spain */
  ES = 'ES',
  /** Sri Lanka */
  LK = 'LK',
  /** Sudan */
  SD = 'SD',
  /** Suriname */
  SR = 'SR',
  /** Svalbard and Jan Mayen */
  SJ = 'SJ',
  /** Sweden */
  SE = 'SE',
  /** Switzerland */
  CH = 'CH',
  /** Syria */
  SY = 'SY',
  /** Taiwan */
  TW = 'TW',
  /** Tajikistan */
  TJ = 'TJ',
  /** Tanzania */
  TZ = 'TZ',
  /** Thailand */
  TH = 'TH',
  /** Timor-Leste */
  TL = 'TL',
  /** Togo */
  TG = 'TG',
  /** Tokelau */
  TK = 'TK',
  /** Tonga */
  TO = 'TO',
  /** Trinidad and Tobago */
  TT = 'TT',
  /** Tunisia */
  TN = 'TN',
  /** Türkiye */
  TR = 'TR',
  /** Turkmenistan */
  TM = 'TM',
  /** Turks and Caicos Islands */
  TC = 'TC',
  /** Tuvalu */
  TV = 'TV',
  /** Uganda */
  UG = 'UG',
  /** Ukraine */
  UA = 'UA',
  /** United Arab Emirates */
  AE = 'AE',
  /** United Kingdom */
  GB = 'GB',
  /** United States Minor Outlying Islands */
  UM = 'UM',
  /** United States of America */
  US = 'US',
  /** Uruguay */
  UY = 'UY',
  /** Uzbekistan */
  UZ = 'UZ',
  /** Vanuatu */
  VU = 'VU',
  /** Venezuela */
  VE = 'VE',
  /** Vietnam */
  VN = 'VN',
  /** Virgin Islands (British) */
  VG = 'VG',
  /** Virgin Islands (U.S.) */
  VI = 'VI',
  /** Wallis and Futuna */
  WF = 'WF',
  /** Western Sahara */
  EH = 'EH',
  /** Yemen */
  YE = 'YE',
  /** Zambia */
  ZM = 'ZM',
  /** Zimbabwe */
  ZW = 'ZW',
}

/** An enumeration. */
export enum UserClientCustomizations {
  DEVENISH = 'DEVENISH',
}

/**
 * Leverages the internal Python implementation of UUID (uuid.UUID) to provide native UUID objects
 * in fields, resolvers and input.
 */
export type UUID = any

export interface Mutation {
  calculate: Calculate
  forgotPassword: ForgotPassword | null
  login: Login | null
  logout: Logout | null
  register: Register | null
  resetPassword: ResetPassword | null
  templateDelete: TemplateDelete | null
  templateSave: TemplateSave | null
  updateCurrentUser: UpdateCurrentUser | null
  ping: String
  __typename: 'Mutation'
}

export enum Units {
  METRIC = 'METRIC',
  IMPERIAL = 'IMPERIAL',
}

export enum ResultsUnitsAmount {
  COW = 'COW',
  HERD = 'HERD',
}

export enum ResultsUnitsPeriod {
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export enum ColumnStatus {
  ON = 'ON',
  OFF = 'OFF',
  ZEROED = 'ZEROED',
}

export interface Calculate {
  currency: Currency
  product: Product
  productPriceConverted: Float
  iofcShortAndLongTerm: Float
  iofcShortTerm: Float
  roiShortAndLongTerm: Float
  roiShortTerm: Float
  weightUnits: String
  investment: CalculationsInvestment
  shortTermReturns: CalculationsShortTermReturns
  longTermReturnsReproduction: CalculationsLongTermReturnsReproduction
  longTermReturnsHealth: CalculationsLongTermReturnsHealth
  __typename: 'Calculate'
}

export interface CalculationsInvestment {
  cowsAmount: ValuesPerPhase
  productPricePerCowDay: ValuesPerPhase
  dmiWithProduct: ValuesPerPhase
  dmCostWithProduct: ValuesPerPhase
  feedCostPerCowDay: ValuesPerPhase
  feedCostPerCowDayWithProduct: ValuesPerPhase
  totalFeedCostPerCowYear: Float
  totalFeedCostPerCowYearWithProduct: Float
  totalFeedCostPerCowYearDifference: Float
  totalFeedCostDifference: Float
  __typename: 'CalculationsInvestment'
}

export interface ValuesPerPhase {
  farOff: Float | null
  closeUp: Float | null
  fresh: Float | null
  earlyMidLact: Float | null
  lateLact: Float | null
  __typename: 'ValuesPerPhase'
}

export interface CalculationsShortTermReturns {
  milkPerCowDayWithProduct: Float
  fatPercent: Float
  proteinPercent: Float
  otherSolidsPercent: Float
  fatPerCowDay: Difference
  proteinPerCowDay: Difference
  otherSolidsPerCowDay: Difference
  ecmPerCowDay: Difference
  impactCwtCost: Difference
  impactPerCowDayCost: Difference
  impactPerCowYearCost: Difference
  totalImpactCost: Difference
  __typename: 'CalculationsShortTermReturns'
}

export interface Difference {
  current: Float | null
  withProduct: Float | null
  difference: Float | null
  __typename: 'Difference'
}

export interface CalculationsLongTermReturnsReproduction {
  rollingHerdAvg: Int
  pregnancyRateWithProduct: Float
  pregnancyLossWithProduct: Float
  calfSales: Difference
  mortalityCost: Difference
  reproductiveCost: Difference
  reproductiveCullingCost: Difference
  nonReproductiveCullingCost: Difference
  totalReturns: Difference
  totalReturnsFinal: Difference
  __typename: 'CalculationsLongTermReturnsReproduction'
}

export interface CalculationsLongTermReturnsHealth {
  mastitis: Difference
  ketosisClinical: Difference
  ketosisSubclinical: Difference
  metritis: Difference
  totalSavings: Difference
  totalSavingsFinal: Difference
  __typename: 'CalculationsLongTermReturnsHealth'
}

export interface ForgotPassword {
  success: Boolean
  __typename: 'ForgotPassword'
}

export interface Login {
  success: Boolean
  __typename: 'Login'
}

export interface Logout {
  success: Boolean
  __typename: 'Logout'
}

export interface Register {
  success: Boolean
  __typename: 'Register'
}

export interface ResetPassword {
  success: Boolean
  __typename: 'ResetPassword'
}

export interface TemplateDelete {
  success: Boolean
  __typename: 'TemplateDelete'
}

export interface TemplateSave {
  success: Boolean
  __typename: 'TemplateSave'
}

export interface UpdateCurrentUser {
  success: Boolean
  __typename: 'UpdateCurrentUser'
}

export interface QueryRequest {
  countries?: [{ language: UserLanguages }, CountryRequest]
  currencies?: CurrencyRequest
  currentUser?: UserRequest
  products?: ProductRequest
  standards?: StandardsRequest
  resetPasswordTokenValid?: [{ token: UUID }]
  ping?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CountryRequest {
  name?: boolean | number
  code?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CurrencyRequest {
  id?: boolean | number
  name?: boolean | number
  code?: boolean | number
  sign?: boolean | number
  conversionRate?: boolean | number
  conversionRateDate?: boolean | number
  sortOrder?: boolean | number
  calculationinputSet?: CalculationInputObjRequest
  standardsregionSet?: StandardsRegionRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationInputObjRequest {
  id?: boolean | number
  units?: boolean | number
  resultsUnitsAmount?: boolean | number
  resultsUnitsPeriod?: boolean | number
  currency?: CurrencyRequest
  product?: ProductRequest
  productPrice?: boolean | number
  region?: StandardsRegionRequest
  herdSize?: boolean | number
  aabCents?: boolean | number
  isPlusDmi?: boolean | number
  isAab?: boolean | number
  investmentFarOffStatus?: boolean | number
  investmentCloseUpStatus?: boolean | number
  investmentFreshStatus?: boolean | number
  investmentEarlyMidLactStatus?: boolean | number
  investmentLateLactStatus?: boolean | number
  investmentHerdStructureFarOff?: boolean | number
  investmentHerdStructureCloseUp?: boolean | number
  investmentHerdStructureFresh?: boolean | number
  investmentHerdStructureEarlyMidLact?: boolean | number
  investmentHerdStructureLateLact?: boolean | number
  investmentProductGramsPerCowDayCloseUp?: boolean | number
  investmentProductGramsPerCowDayFresh?: boolean | number
  investmentProductGramsPerCowDayEarlyMidLact?: boolean | number
  investmentProductGramsPerCowDayLateLact?: boolean | number
  investmentDmiFarOff?: boolean | number
  investmentDmiCloseUp?: boolean | number
  investmentDmiFresh?: boolean | number
  investmentDmiEarlyMidLact?: boolean | number
  investmentDmiLateLact?: boolean | number
  investmentDmCostCurrentFarOff?: boolean | number
  investmentDmCostCurrentCloseUp?: boolean | number
  investmentDmCostCurrentFresh?: boolean | number
  investmentDmCostCurrentEarlyMidLact?: boolean | number
  investmentDmCostCurrentLateLact?: boolean | number
  shortTermReturnsMilkCurrent?: boolean | number
  shortTermReturnsMilkDifference?: boolean | number
  shortTermReturnsMilkFmmoPrice?: boolean | number
  shortTermReturnsFatCurrent?: boolean | number
  shortTermReturnsFatDifference?: boolean | number
  shortTermReturnsFatFmmoPrice?: boolean | number
  shortTermReturnsProteinCurrent?: boolean | number
  shortTermReturnsProteinDifference?: boolean | number
  shortTermReturnsProteinFmmoPrice?: boolean | number
  shortTermReturnsOtherSolidsCurrent?: boolean | number
  shortTermReturnsOtherSolidsDifference?: boolean | number
  shortTermReturnsOtherSolidsFmmoPrice?: boolean | number
  shortTermReturnsPremiumFmmoPrice?: boolean | number
  longTermReturnsReproductionCostCurrent?: boolean | number
  longTermReturnsReplacementCostCurrent?: boolean | number
  longTermReturnsCullValueCurrent?: boolean | number
  longTermReturnsCalfValueCurrent?: boolean | number
  longTermReturnsLastMonthToBreedCurrent?: boolean | number
  longTermReturnsMinimumMilkToBreedCurrent?: boolean | number
  longTermReturnsCullingRateCurrent?: boolean | number
  longTermReturnsPregnancyRateCurrent?: boolean | number
  longTermReturnsPregnancyRateDifference?: boolean | number
  longTermReturnsPregnancyLossCurrent?: boolean | number
  longTermReturnsPregnancyLossDifference?: boolean | number
  longTermReturnsMastitisRegular?: boolean | number
  longTermReturnsMastitisReduced?: boolean | number
  longTermReturnsMastitisCostPerCase?: boolean | number
  longTermReturnsKetosisClinicalRegular?: boolean | number
  longTermReturnsKetosisClinicalReduced?: boolean | number
  longTermReturnsKetosisClinicalCostPerCase?: boolean | number
  longTermReturnsKetosisSubClinicalRegular?: boolean | number
  longTermReturnsKetosisSubClinicalReduced?: boolean | number
  longTermReturnsKetosisSubclinicalCostPerCase?: boolean | number
  longTermReturnsMetritisRegular?: boolean | number
  longTermReturnsMetritisReduced?: boolean | number
  longTermReturnsMetritisCostPerCase?: boolean | number
  calculationtemplate?: CalculationTemplateRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ProductRequest {
  id?: boolean | number
  price?: boolean | number
  name?: boolean | number
  metrics?: ProductMetricsRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ProductMetricsRequest {
  standards?: StandardsRequest
  economicPerformance?: EconomicPerformanceRequest
  milkPerformance?: MilkPerformanceRequest
  milkPerformanceDiff?: MilkPerformanceDiffRequest
  productFeedingRates?: ProductFeedingRatesRequest
  reproductivePerformance?: ReproductivePerformanceRequest
  diseasePerformances?: DiseasePerformanceRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface StandardsRequest {
  id?: boolean | number
  region?: StandardsRegionRequest
  fmmoPricing?: FmmoPricingRequest
  herdComposition?: ConstantsPerPhaseRequest
  dmi?: ConstantsPerPhaseRequest
  currentFeedCosts?: ConstantsPerPhaseRequest
  diseaseMetrics?: DiseaseMetricsRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface StandardsRegionRequest {
  id?: boolean | number
  countries?: CountryRequest
  currency?: CurrencyRequest
  forClient?: boolean | number
  name?: boolean | number
  units?: boolean | number
  calculationinputSet?: CalculationInputObjRequest
  standards?: StandardsRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FmmoPricingRequest {
  milk?: boolean | number
  fat?: boolean | number
  protein?: boolean | number
  otherSolids?: boolean | number
  premium?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ConstantsPerPhaseRequest {
  farOff?: boolean | number
  closeUp?: boolean | number
  fresh?: boolean | number
  earlyMidLact?: boolean | number
  lateLact?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface DiseaseMetricsRequest {
  disease?: boolean | number
  usdCase?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface EconomicPerformanceRequest {
  reproductionCost?: boolean | number
  replacementCost?: boolean | number
  cullValue?: boolean | number
  calfValue?: boolean | number
  cullingRate?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface MilkPerformanceRequest {
  milk?: boolean | number
  fat?: boolean | number
  protein?: boolean | number
  otherSolids?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface MilkPerformanceDiffRequest {
  milk?: boolean | number
  fat?: boolean | number
  protein?: boolean | number
  otherSolids?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ProductFeedingRatesRequest {
  farOff?: boolean | number
  closeUp?: boolean | number
  fresh?: boolean | number
  earlyMidLact?: boolean | number
  lateLact?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ReproductivePerformanceRequest {
  lastMonthToBreed?: boolean | number
  minimumMilkToBreed?: boolean | number
  pregnancyRate?: boolean | number
  pregnancyRateDiff?: boolean | number
  pregnancyLoss?: boolean | number
  pregnancyLossDiff?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface DiseasePerformanceRequest {
  disease?: boolean | number
  regularIncidence?: boolean | number
  reducedIncidence?: boolean | number
  reductionPercent?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationTemplateRequest {
  id?: boolean | number
  calculationInput?: CalculationInputObjRequest
  name?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface UserRequest {
  defaultCalculationTemplate?: CalculationTemplateRequest
  email?: boolean | number
  language?: boolean | number
  country?: boolean | number
  clientCustomization?: boolean | number
  calculationTemplates?: CalculationTemplateRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface MutationRequest {
  calculate?: [{ input: CalculationInput }, CalculateRequest]
  forgotPassword?: [{ email: String; language: UserLanguages }, ForgotPasswordRequest]
  login?: [{ email: String; password: String }, LoginRequest]
  logout?: LogoutRequest
  register?: [
    {
      adisseoContactName: String
      company: String
      country: String
      email: String
      language: UserLanguages
      name: String
      password: String
      password2: String
    },
    RegisterRequest,
  ]
  resetPassword?: [{ language: UserLanguages; password: String; password2: String; token: UUID }, ResetPasswordRequest]
  templateDelete?: [{ id: ID }, TemplateDeleteRequest]
  templateSave?: [{ calculationInput: CalculationInput; name: String }, TemplateSaveRequest]
  updateCurrentUser?:
    | [
        { defaultCalculationTemplateId?: Int | null; language?: UserLanguages | null; name?: String | null },
        UpdateCurrentUserRequest,
      ]
    | UpdateCurrentUserRequest
  ping?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationInput {
  units: Units
  resultsUnitsAmount: ResultsUnitsAmount
  resultsUnitsPeriod: ResultsUnitsPeriod
  currencyId: ID
  productId: ID
  productPrice: Float
  regionId: ID
  herdSize: Int
  aabCents: Int
  isPlusDmi: Boolean
  isAab: Boolean
  investmentFarOffStatus: ColumnStatus
  investmentCloseUpStatus: ColumnStatus
  investmentFreshStatus: ColumnStatus
  investmentEarlyMidLactStatus: ColumnStatus
  investmentLateLactStatus: ColumnStatus
  investmentHerdStructureFarOff: Float
  investmentHerdStructureCloseUp: Float
  investmentHerdStructureFresh: Float
  investmentHerdStructureEarlyMidLact: Float
  investmentHerdStructureLateLact: Float
  investmentProductGramsPerCowDayCloseUp: Float
  investmentProductGramsPerCowDayFresh: Float
  investmentProductGramsPerCowDayEarlyMidLact: Float
  investmentProductGramsPerCowDayLateLact: Float
  investmentDmiFarOff: Float
  investmentDmiCloseUp: Float
  investmentDmiFresh: Float
  investmentDmiEarlyMidLact: Float
  investmentDmiLateLact: Float
  investmentDmCostCurrentFarOff: Float
  investmentDmCostCurrentCloseUp: Float
  investmentDmCostCurrentFresh: Float
  investmentDmCostCurrentEarlyMidLact: Float
  investmentDmCostCurrentLateLact: Float
  shortTermReturnsMilkCurrent: Float
  shortTermReturnsMilkDifference: Float
  shortTermReturnsMilkFmmoPrice: Float
  shortTermReturnsFatCurrent: Float
  shortTermReturnsFatDifference: Float
  shortTermReturnsFatFmmoPrice: Float
  shortTermReturnsProteinCurrent: Float
  shortTermReturnsProteinDifference: Float
  shortTermReturnsProteinFmmoPrice: Float
  shortTermReturnsOtherSolidsCurrent: Float
  shortTermReturnsOtherSolidsDifference: Float
  shortTermReturnsOtherSolidsFmmoPrice: Float
  shortTermReturnsPremiumFmmoPrice: Float
  longTermReturnsReproductionCostCurrent: Float
  longTermReturnsReplacementCostCurrent: Float
  longTermReturnsCullValueCurrent: Float
  longTermReturnsCalfValueCurrent: Float
  longTermReturnsLastMonthToBreedCurrent: Float
  longTermReturnsMinimumMilkToBreedCurrent: Float
  longTermReturnsCullingRateCurrent: Float
  longTermReturnsPregnancyRateCurrent: Float
  longTermReturnsPregnancyRateDifference: Float
  longTermReturnsPregnancyLossCurrent: Float
  longTermReturnsPregnancyLossDifference: Float
  longTermReturnsMastitisRegular: Float
  longTermReturnsMastitisReduced: Float
  longTermReturnsMastitisCostPerCase: Float
  longTermReturnsKetosisClinicalRegular: Float
  longTermReturnsKetosisClinicalReduced: Float
  longTermReturnsKetosisClinicalCostPerCase: Float
  longTermReturnsKetosisSubClinicalRegular: Float
  longTermReturnsKetosisSubClinicalReduced: Float
  longTermReturnsKetosisSubclinicalCostPerCase: Float
  longTermReturnsMetritisRegular: Float
  longTermReturnsMetritisReduced: Float
  longTermReturnsMetritisCostPerCase: Float
}

export interface CalculateRequest {
  currency?: CurrencyRequest
  product?: ProductRequest
  productPriceConverted?: boolean | number
  iofcShortAndLongTerm?: boolean | number
  iofcShortTerm?: boolean | number
  roiShortAndLongTerm?: boolean | number
  roiShortTerm?: boolean | number
  weightUnits?: boolean | number
  investment?: CalculationsInvestmentRequest
  shortTermReturns?: CalculationsShortTermReturnsRequest
  longTermReturnsReproduction?: CalculationsLongTermReturnsReproductionRequest
  longTermReturnsHealth?: CalculationsLongTermReturnsHealthRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationsInvestmentRequest {
  cowsAmount?: ValuesPerPhaseRequest
  productPricePerCowDay?: ValuesPerPhaseRequest
  dmiWithProduct?: ValuesPerPhaseRequest
  dmCostWithProduct?: ValuesPerPhaseRequest
  feedCostPerCowDay?: ValuesPerPhaseRequest
  feedCostPerCowDayWithProduct?: ValuesPerPhaseRequest
  totalFeedCostPerCowYear?: boolean | number
  totalFeedCostPerCowYearWithProduct?: boolean | number
  totalFeedCostPerCowYearDifference?: boolean | number
  totalFeedCostDifference?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ValuesPerPhaseRequest {
  farOff?: boolean | number
  closeUp?: boolean | number
  fresh?: boolean | number
  earlyMidLact?: boolean | number
  lateLact?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationsShortTermReturnsRequest {
  milkPerCowDayWithProduct?: boolean | number
  fatPercent?: boolean | number
  proteinPercent?: boolean | number
  otherSolidsPercent?: boolean | number
  fatPerCowDay?: DifferenceRequest
  proteinPerCowDay?: DifferenceRequest
  otherSolidsPerCowDay?: DifferenceRequest
  ecmPerCowDay?: DifferenceRequest
  impactCwtCost?: DifferenceRequest
  impactPerCowDayCost?: DifferenceRequest
  impactPerCowYearCost?: DifferenceRequest
  totalImpactCost?: DifferenceRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface DifferenceRequest {
  current?: boolean | number
  withProduct?: boolean | number
  difference?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationsLongTermReturnsReproductionRequest {
  rollingHerdAvg?: boolean | number
  pregnancyRateWithProduct?: boolean | number
  pregnancyLossWithProduct?: boolean | number
  calfSales?: DifferenceRequest
  mortalityCost?: DifferenceRequest
  reproductiveCost?: DifferenceRequest
  reproductiveCullingCost?: DifferenceRequest
  nonReproductiveCullingCost?: DifferenceRequest
  totalReturns?: DifferenceRequest
  totalReturnsFinal?: DifferenceRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CalculationsLongTermReturnsHealthRequest {
  mastitis?: DifferenceRequest
  ketosisClinical?: DifferenceRequest
  ketosisSubclinical?: DifferenceRequest
  metritis?: DifferenceRequest
  totalSavings?: DifferenceRequest
  totalSavingsFinal?: DifferenceRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ForgotPasswordRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface LoginRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface LogoutRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface RegisterRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ResetPasswordRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface TemplateDeleteRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface TemplateSaveRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface UpdateCurrentUserRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

const Query_possibleTypes = ['Query']
export const isQuery = (obj: { __typename: String }): obj is Query => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Query_possibleTypes.includes(obj.__typename)
}

const Country_possibleTypes = ['Country']
export const isCountry = (obj: { __typename: String }): obj is Country => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Country_possibleTypes.includes(obj.__typename)
}

const Currency_possibleTypes = ['Currency']
export const isCurrency = (obj: { __typename: String }): obj is Currency => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Currency_possibleTypes.includes(obj.__typename)
}

const CalculationInputObj_possibleTypes = ['CalculationInputObj']
export const isCalculationInputObj = (obj: { __typename: String }): obj is CalculationInputObj => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationInputObj_possibleTypes.includes(obj.__typename)
}

const Product_possibleTypes = ['Product']
export const isProduct = (obj: { __typename: String }): obj is Product => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Product_possibleTypes.includes(obj.__typename)
}

const ProductMetrics_possibleTypes = ['ProductMetrics']
export const isProductMetrics = (obj: { __typename: String }): obj is ProductMetrics => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ProductMetrics_possibleTypes.includes(obj.__typename)
}

const Standards_possibleTypes = ['Standards']
export const isStandards = (obj: { __typename: String }): obj is Standards => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Standards_possibleTypes.includes(obj.__typename)
}

const StandardsRegion_possibleTypes = ['StandardsRegion']
export const isStandardsRegion = (obj: { __typename: String }): obj is StandardsRegion => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return StandardsRegion_possibleTypes.includes(obj.__typename)
}

const FmmoPricing_possibleTypes = ['FmmoPricing']
export const isFmmoPricing = (obj: { __typename: String }): obj is FmmoPricing => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return FmmoPricing_possibleTypes.includes(obj.__typename)
}

const ConstantsPerPhase_possibleTypes = ['ConstantsPerPhase']
export const isConstantsPerPhase = (obj: { __typename: String }): obj is ConstantsPerPhase => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ConstantsPerPhase_possibleTypes.includes(obj.__typename)
}

const DiseaseMetrics_possibleTypes = ['DiseaseMetrics']
export const isDiseaseMetrics = (obj: { __typename: String }): obj is DiseaseMetrics => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return DiseaseMetrics_possibleTypes.includes(obj.__typename)
}

const EconomicPerformance_possibleTypes = ['EconomicPerformance']
export const isEconomicPerformance = (obj: { __typename: String }): obj is EconomicPerformance => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return EconomicPerformance_possibleTypes.includes(obj.__typename)
}

const MilkPerformance_possibleTypes = ['MilkPerformance']
export const isMilkPerformance = (obj: { __typename: String }): obj is MilkPerformance => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return MilkPerformance_possibleTypes.includes(obj.__typename)
}

const MilkPerformanceDiff_possibleTypes = ['MilkPerformanceDiff']
export const isMilkPerformanceDiff = (obj: { __typename: String }): obj is MilkPerformanceDiff => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return MilkPerformanceDiff_possibleTypes.includes(obj.__typename)
}

const ProductFeedingRates_possibleTypes = ['ProductFeedingRates']
export const isProductFeedingRates = (obj: { __typename: String }): obj is ProductFeedingRates => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ProductFeedingRates_possibleTypes.includes(obj.__typename)
}

const ReproductivePerformance_possibleTypes = ['ReproductivePerformance']
export const isReproductivePerformance = (obj: { __typename: String }): obj is ReproductivePerformance => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ReproductivePerformance_possibleTypes.includes(obj.__typename)
}

const DiseasePerformance_possibleTypes = ['DiseasePerformance']
export const isDiseasePerformance = (obj: { __typename: String }): obj is DiseasePerformance => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return DiseasePerformance_possibleTypes.includes(obj.__typename)
}

const CalculationTemplate_possibleTypes = ['CalculationTemplate']
export const isCalculationTemplate = (obj: { __typename: String }): obj is CalculationTemplate => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationTemplate_possibleTypes.includes(obj.__typename)
}

const User_possibleTypes = ['User']
export const isUser = (obj: { __typename: String }): obj is User => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return User_possibleTypes.includes(obj.__typename)
}

const Mutation_possibleTypes = ['Mutation']
export const isMutation = (obj: { __typename: String }): obj is Mutation => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Mutation_possibleTypes.includes(obj.__typename)
}

const Calculate_possibleTypes = ['Calculate']
export const isCalculate = (obj: { __typename: String }): obj is Calculate => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Calculate_possibleTypes.includes(obj.__typename)
}

const CalculationsInvestment_possibleTypes = ['CalculationsInvestment']
export const isCalculationsInvestment = (obj: { __typename: String }): obj is CalculationsInvestment => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationsInvestment_possibleTypes.includes(obj.__typename)
}

const ValuesPerPhase_possibleTypes = ['ValuesPerPhase']
export const isValuesPerPhase = (obj: { __typename: String }): obj is ValuesPerPhase => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ValuesPerPhase_possibleTypes.includes(obj.__typename)
}

const CalculationsShortTermReturns_possibleTypes = ['CalculationsShortTermReturns']
export const isCalculationsShortTermReturns = (obj: { __typename: String }): obj is CalculationsShortTermReturns => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationsShortTermReturns_possibleTypes.includes(obj.__typename)
}

const Difference_possibleTypes = ['Difference']
export const isDifference = (obj: { __typename: String }): obj is Difference => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Difference_possibleTypes.includes(obj.__typename)
}

const CalculationsLongTermReturnsReproduction_possibleTypes = ['CalculationsLongTermReturnsReproduction']
export const isCalculationsLongTermReturnsReproduction = (obj: {
  __typename: String
}): obj is CalculationsLongTermReturnsReproduction => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationsLongTermReturnsReproduction_possibleTypes.includes(obj.__typename)
}

const CalculationsLongTermReturnsHealth_possibleTypes = ['CalculationsLongTermReturnsHealth']
export const isCalculationsLongTermReturnsHealth = (obj: {
  __typename: String
}): obj is CalculationsLongTermReturnsHealth => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CalculationsLongTermReturnsHealth_possibleTypes.includes(obj.__typename)
}

const ForgotPassword_possibleTypes = ['ForgotPassword']
export const isForgotPassword = (obj: { __typename: String }): obj is ForgotPassword => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ForgotPassword_possibleTypes.includes(obj.__typename)
}

const Login_possibleTypes = ['Login']
export const isLogin = (obj: { __typename: String }): obj is Login => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Login_possibleTypes.includes(obj.__typename)
}

const Logout_possibleTypes = ['Logout']
export const isLogout = (obj: { __typename: String }): obj is Logout => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Logout_possibleTypes.includes(obj.__typename)
}

const Register_possibleTypes = ['Register']
export const isRegister = (obj: { __typename: String }): obj is Register => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Register_possibleTypes.includes(obj.__typename)
}

const ResetPassword_possibleTypes = ['ResetPassword']
export const isResetPassword = (obj: { __typename: String }): obj is ResetPassword => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ResetPassword_possibleTypes.includes(obj.__typename)
}

const TemplateDelete_possibleTypes = ['TemplateDelete']
export const isTemplateDelete = (obj: { __typename: String }): obj is TemplateDelete => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return TemplateDelete_possibleTypes.includes(obj.__typename)
}

const TemplateSave_possibleTypes = ['TemplateSave']
export const isTemplateSave = (obj: { __typename: String }): obj is TemplateSave => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return TemplateSave_possibleTypes.includes(obj.__typename)
}

const UpdateCurrentUser_possibleTypes = ['UpdateCurrentUser']
export const isUpdateCurrentUser = (obj: { __typename: String }): obj is UpdateCurrentUser => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return UpdateCurrentUser_possibleTypes.includes(obj.__typename)
}

export interface QueryPromiseChain {
  countries: (args: {
    language: UserLanguages
  }) => { execute: (request: CountryRequest, defaultValue?: Country[]) => Promise<Country[]> }
  currencies: { execute: (request: CurrencyRequest, defaultValue?: Currency[]) => Promise<Currency[]> }
  currentUser: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> }
  products: { execute: (request: ProductRequest, defaultValue?: Product[]) => Promise<Product[]> }
  standards: { execute: (request: StandardsRequest, defaultValue?: Standards[]) => Promise<Standards[]> }
  resetPasswordTokenValid: (args: {
    token: UUID
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  ping: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface QueryObservableChain {
  countries: (args: {
    language: UserLanguages
  }) => { execute: (request: CountryRequest, defaultValue?: Country[]) => Observable<Country[]> }
  currencies: { execute: (request: CurrencyRequest, defaultValue?: Currency[]) => Observable<Currency[]> }
  currentUser: UserObservableChain & {
    execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null>
  }
  products: { execute: (request: ProductRequest, defaultValue?: Product[]) => Observable<Product[]> }
  standards: { execute: (request: StandardsRequest, defaultValue?: Standards[]) => Observable<Standards[]> }
  resetPasswordTokenValid: (args: {
    token: UUID
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  ping: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface CountryPromiseChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  code: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface CountryObservableChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  code: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface CurrencyPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  code: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  sign: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  conversionRate: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  conversionRateDate: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  sortOrder: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  calculationinputSet: {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj[]) => Promise<CalculationInputObj[]>
  }
  standardsregionSet: {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion[]) => Promise<StandardsRegion[]>
  }
}

export interface CurrencyObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  code: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  sign: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  conversionRate: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  conversionRateDate: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  sortOrder: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  calculationinputSet: {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj[]) => Observable<CalculationInputObj[]>
  }
  standardsregionSet: {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion[]) => Observable<StandardsRegion[]>
  }
}

export interface CalculationInputObjPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  units: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputUnitsChoices,
    ) => Promise<MainCalculationInputUnitsChoices>
  }
  resultsUnitsAmount: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputResultsUnitsAmountChoices,
    ) => Promise<MainCalculationInputResultsUnitsAmountChoices>
  }
  resultsUnitsPeriod: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputResultsUnitsPeriodChoices,
    ) => Promise<MainCalculationInputResultsUnitsPeriodChoices>
  }
  currency: CurrencyPromiseChain & { execute: (request: CurrencyRequest, defaultValue?: Currency) => Promise<Currency> }
  product: ProductPromiseChain & { execute: (request: ProductRequest, defaultValue?: Product) => Promise<Product> }
  productPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  region: StandardsRegionPromiseChain & {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion) => Promise<StandardsRegion>
  }
  herdSize: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  aabCents: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  isPlusDmi: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  isAab: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  investmentFarOffStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentFarOffStatusChoices,
    ) => Promise<MainCalculationInputInvestmentFarOffStatusChoices>
  }
  investmentCloseUpStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentCloseUpStatusChoices,
    ) => Promise<MainCalculationInputInvestmentCloseUpStatusChoices>
  }
  investmentFreshStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentFreshStatusChoices,
    ) => Promise<MainCalculationInputInvestmentFreshStatusChoices>
  }
  investmentEarlyMidLactStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentEarlyMidLactStatusChoices,
    ) => Promise<MainCalculationInputInvestmentEarlyMidLactStatusChoices>
  }
  investmentLateLactStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentLateLactStatusChoices,
    ) => Promise<MainCalculationInputInvestmentLateLactStatusChoices>
  }
  investmentHerdStructureFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentHerdStructureCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentHerdStructureFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentHerdStructureEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentHerdStructureLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentProductGramsPerCowDayCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentProductGramsPerCowDayFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentProductGramsPerCowDayEarlyMidLact: {
    execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float>
  }
  investmentProductGramsPerCowDayLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmiFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmiCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmiFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmiEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmiLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmCostCurrentFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmCostCurrentCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmCostCurrentFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmCostCurrentEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentDmCostCurrentLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsMilkCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsMilkDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsMilkFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsFatCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsFatDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsFatFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsProteinCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsProteinDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsProteinFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsOtherSolidsCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsOtherSolidsDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsOtherSolidsFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  shortTermReturnsPremiumFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsReproductionCostCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsReplacementCostCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsCullValueCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsCalfValueCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsLastMonthToBreedCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMinimumMilkToBreedCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsCullingRateCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsPregnancyRateCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsPregnancyRateDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsPregnancyLossCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsPregnancyLossDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMastitisRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMastitisReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMastitisCostPerCase: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsKetosisClinicalRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsKetosisClinicalReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsKetosisClinicalCostPerCase: {
    execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float>
  }
  longTermReturnsKetosisSubClinicalRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsKetosisSubClinicalReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsKetosisSubclinicalCostPerCase: {
    execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float>
  }
  longTermReturnsMetritisRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMetritisReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  longTermReturnsMetritisCostPerCase: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  calculationtemplate: CalculationTemplatePromiseChain & {
    execute: (
      request: CalculationTemplateRequest,
      defaultValue?: CalculationTemplate | null,
    ) => Promise<CalculationTemplate | null>
  }
}

export interface CalculationInputObjObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  units: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputUnitsChoices,
    ) => Observable<MainCalculationInputUnitsChoices>
  }
  resultsUnitsAmount: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputResultsUnitsAmountChoices,
    ) => Observable<MainCalculationInputResultsUnitsAmountChoices>
  }
  resultsUnitsPeriod: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputResultsUnitsPeriodChoices,
    ) => Observable<MainCalculationInputResultsUnitsPeriodChoices>
  }
  currency: CurrencyObservableChain & {
    execute: (request: CurrencyRequest, defaultValue?: Currency) => Observable<Currency>
  }
  product: ProductObservableChain & { execute: (request: ProductRequest, defaultValue?: Product) => Observable<Product> }
  productPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  region: StandardsRegionObservableChain & {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion) => Observable<StandardsRegion>
  }
  herdSize: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  aabCents: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  isPlusDmi: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  isAab: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  investmentFarOffStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentFarOffStatusChoices,
    ) => Observable<MainCalculationInputInvestmentFarOffStatusChoices>
  }
  investmentCloseUpStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentCloseUpStatusChoices,
    ) => Observable<MainCalculationInputInvestmentCloseUpStatusChoices>
  }
  investmentFreshStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentFreshStatusChoices,
    ) => Observable<MainCalculationInputInvestmentFreshStatusChoices>
  }
  investmentEarlyMidLactStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentEarlyMidLactStatusChoices,
    ) => Observable<MainCalculationInputInvestmentEarlyMidLactStatusChoices>
  }
  investmentLateLactStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainCalculationInputInvestmentLateLactStatusChoices,
    ) => Observable<MainCalculationInputInvestmentLateLactStatusChoices>
  }
  investmentHerdStructureFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentHerdStructureCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentHerdStructureFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentHerdStructureEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentHerdStructureLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentProductGramsPerCowDayCloseUp: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  investmentProductGramsPerCowDayFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentProductGramsPerCowDayEarlyMidLact: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  investmentProductGramsPerCowDayLateLact: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  investmentDmiFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmiCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmiFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmiEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmiLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmCostCurrentFarOff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmCostCurrentCloseUp: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmCostCurrentFresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmCostCurrentEarlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentDmCostCurrentLateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsMilkCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsMilkDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsMilkFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsFatCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsFatDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsFatFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsProteinCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsProteinDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsProteinFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsOtherSolidsCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsOtherSolidsDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsOtherSolidsFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  shortTermReturnsPremiumFmmoPrice: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsReproductionCostCurrent: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsReplacementCostCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsCullValueCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsCalfValueCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsLastMonthToBreedCurrent: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsMinimumMilkToBreedCurrent: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsCullingRateCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsPregnancyRateCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsPregnancyRateDifference: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsPregnancyLossCurrent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsPregnancyLossDifference: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsMastitisRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsMastitisReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsMastitisCostPerCase: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsKetosisClinicalRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsKetosisClinicalReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsKetosisClinicalCostPerCase: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsKetosisSubClinicalRegular: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsKetosisSubClinicalReduced: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsKetosisSubclinicalCostPerCase: {
    execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float>
  }
  longTermReturnsMetritisRegular: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsMetritisReduced: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  longTermReturnsMetritisCostPerCase: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  calculationtemplate: CalculationTemplateObservableChain & {
    execute: (
      request: CalculationTemplateRequest,
      defaultValue?: CalculationTemplate | null,
    ) => Observable<CalculationTemplate | null>
  }
}

export interface ProductPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  price: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  metrics: { execute: (request: ProductMetricsRequest, defaultValue?: ProductMetrics[]) => Promise<ProductMetrics[]> }
}

export interface ProductObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  price: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  metrics: { execute: (request: ProductMetricsRequest, defaultValue?: ProductMetrics[]) => Observable<ProductMetrics[]> }
}

export interface ProductMetricsPromiseChain {
  standards: StandardsPromiseChain & { execute: (request: StandardsRequest, defaultValue?: Standards) => Promise<Standards> }
  economicPerformance: EconomicPerformancePromiseChain & {
    execute: (request: EconomicPerformanceRequest, defaultValue?: EconomicPerformance) => Promise<EconomicPerformance>
  }
  milkPerformance: MilkPerformancePromiseChain & {
    execute: (request: MilkPerformanceRequest, defaultValue?: MilkPerformance) => Promise<MilkPerformance>
  }
  milkPerformanceDiff: MilkPerformanceDiffPromiseChain & {
    execute: (request: MilkPerformanceDiffRequest, defaultValue?: MilkPerformanceDiff) => Promise<MilkPerformanceDiff>
  }
  productFeedingRates: ProductFeedingRatesPromiseChain & {
    execute: (request: ProductFeedingRatesRequest, defaultValue?: ProductFeedingRates) => Promise<ProductFeedingRates>
  }
  reproductivePerformance: ReproductivePerformancePromiseChain & {
    execute: (
      request: ReproductivePerformanceRequest,
      defaultValue?: ReproductivePerformance,
    ) => Promise<ReproductivePerformance>
  }
  diseasePerformances: {
    execute: (request: DiseasePerformanceRequest, defaultValue?: DiseasePerformance[]) => Promise<DiseasePerformance[]>
  }
}

export interface ProductMetricsObservableChain {
  standards: StandardsObservableChain & {
    execute: (request: StandardsRequest, defaultValue?: Standards) => Observable<Standards>
  }
  economicPerformance: EconomicPerformanceObservableChain & {
    execute: (request: EconomicPerformanceRequest, defaultValue?: EconomicPerformance) => Observable<EconomicPerformance>
  }
  milkPerformance: MilkPerformanceObservableChain & {
    execute: (request: MilkPerformanceRequest, defaultValue?: MilkPerformance) => Observable<MilkPerformance>
  }
  milkPerformanceDiff: MilkPerformanceDiffObservableChain & {
    execute: (request: MilkPerformanceDiffRequest, defaultValue?: MilkPerformanceDiff) => Observable<MilkPerformanceDiff>
  }
  productFeedingRates: ProductFeedingRatesObservableChain & {
    execute: (request: ProductFeedingRatesRequest, defaultValue?: ProductFeedingRates) => Observable<ProductFeedingRates>
  }
  reproductivePerformance: ReproductivePerformanceObservableChain & {
    execute: (
      request: ReproductivePerformanceRequest,
      defaultValue?: ReproductivePerformance,
    ) => Observable<ReproductivePerformance>
  }
  diseasePerformances: {
    execute: (request: DiseasePerformanceRequest, defaultValue?: DiseasePerformance[]) => Observable<DiseasePerformance[]>
  }
}

export interface StandardsPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  region: StandardsRegionPromiseChain & {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion) => Promise<StandardsRegion>
  }
  fmmoPricing: FmmoPricingPromiseChain & {
    execute: (request: FmmoPricingRequest, defaultValue?: FmmoPricing) => Promise<FmmoPricing>
  }
  herdComposition: ConstantsPerPhasePromiseChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Promise<ConstantsPerPhase>
  }
  dmi: ConstantsPerPhasePromiseChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Promise<ConstantsPerPhase>
  }
  currentFeedCosts: ConstantsPerPhasePromiseChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Promise<ConstantsPerPhase>
  }
  diseaseMetrics: { execute: (request: DiseaseMetricsRequest, defaultValue?: DiseaseMetrics[]) => Promise<DiseaseMetrics[]> }
}

export interface StandardsObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  region: StandardsRegionObservableChain & {
    execute: (request: StandardsRegionRequest, defaultValue?: StandardsRegion) => Observable<StandardsRegion>
  }
  fmmoPricing: FmmoPricingObservableChain & {
    execute: (request: FmmoPricingRequest, defaultValue?: FmmoPricing) => Observable<FmmoPricing>
  }
  herdComposition: ConstantsPerPhaseObservableChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Observable<ConstantsPerPhase>
  }
  dmi: ConstantsPerPhaseObservableChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Observable<ConstantsPerPhase>
  }
  currentFeedCosts: ConstantsPerPhaseObservableChain & {
    execute: (request: ConstantsPerPhaseRequest, defaultValue?: ConstantsPerPhase) => Observable<ConstantsPerPhase>
  }
  diseaseMetrics: {
    execute: (request: DiseaseMetricsRequest, defaultValue?: DiseaseMetrics[]) => Observable<DiseaseMetrics[]>
  }
}

export interface StandardsRegionPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  countries: { execute: (request: CountryRequest, defaultValue?: Country[]) => Promise<Country[]> }
  currency: CurrencyPromiseChain & { execute: (request: CurrencyRequest, defaultValue?: Currency) => Promise<Currency> }
  forClient: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainStandardsRegionForClientChoices | null,
    ) => Promise<MainStandardsRegionForClientChoices | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  units: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainStandardsRegionUnitsChoices,
    ) => Promise<MainStandardsRegionUnitsChoices>
  }
  calculationinputSet: {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj[]) => Promise<CalculationInputObj[]>
  }
  standards: StandardsPromiseChain & {
    execute: (request: StandardsRequest, defaultValue?: Standards | null) => Promise<Standards | null>
  }
}

export interface StandardsRegionObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  countries: { execute: (request: CountryRequest, defaultValue?: Country[]) => Observable<Country[]> }
  currency: CurrencyObservableChain & {
    execute: (request: CurrencyRequest, defaultValue?: Currency) => Observable<Currency>
  }
  forClient: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainStandardsRegionForClientChoices | null,
    ) => Observable<MainStandardsRegionForClientChoices | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  units: {
    execute: (
      request?: boolean | number,
      defaultValue?: MainStandardsRegionUnitsChoices,
    ) => Observable<MainStandardsRegionUnitsChoices>
  }
  calculationinputSet: {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj[]) => Observable<CalculationInputObj[]>
  }
  standards: StandardsObservableChain & {
    execute: (request: StandardsRequest, defaultValue?: Standards | null) => Observable<Standards | null>
  }
}

export interface FmmoPricingPromiseChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  premium: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface FmmoPricingObservableChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  premium: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface ConstantsPerPhasePromiseChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface ConstantsPerPhaseObservableChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface DiseaseMetricsPromiseChain {
  disease: { execute: (request?: boolean | number, defaultValue?: DiseaseMetricsDisease) => Promise<DiseaseMetricsDisease> }
  usdCase: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface DiseaseMetricsObservableChain {
  disease: {
    execute: (request?: boolean | number, defaultValue?: DiseaseMetricsDisease) => Observable<DiseaseMetricsDisease>
  }
  usdCase: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface EconomicPerformancePromiseChain {
  reproductionCost: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  replacementCost: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  cullValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  calfValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  cullingRate: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface EconomicPerformanceObservableChain {
  reproductionCost: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  replacementCost: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  cullValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  calfValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  cullingRate: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface MilkPerformancePromiseChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface MilkPerformanceObservableChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface MilkPerformanceDiffPromiseChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface MilkPerformanceDiffObservableChain {
  milk: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fat: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  protein: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  otherSolids: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface ProductFeedingRatesPromiseChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface ProductFeedingRatesObservableChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface ReproductivePerformancePromiseChain {
  lastMonthToBreed: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  minimumMilkToBreed: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  pregnancyRate: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  pregnancyRateDiff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  pregnancyLoss: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  pregnancyLossDiff: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface ReproductivePerformanceObservableChain {
  lastMonthToBreed: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  minimumMilkToBreed: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  pregnancyRate: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  pregnancyRateDiff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  pregnancyLoss: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  pregnancyLossDiff: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface DiseasePerformancePromiseChain {
  disease: {
    execute: (request?: boolean | number, defaultValue?: DiseasePerformanceDisease) => Promise<DiseasePerformanceDisease>
  }
  regularIncidence: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  reducedIncidence: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  reductionPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface DiseasePerformanceObservableChain {
  disease: {
    execute: (request?: boolean | number, defaultValue?: DiseasePerformanceDisease) => Observable<DiseasePerformanceDisease>
  }
  regularIncidence: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  reducedIncidence: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  reductionPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface CalculationTemplatePromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Promise<ID> }
  calculationInput: CalculationInputObjPromiseChain & {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj) => Promise<CalculationInputObj>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface CalculationTemplateObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: ID) => Observable<ID> }
  calculationInput: CalculationInputObjObservableChain & {
    execute: (request: CalculationInputObjRequest, defaultValue?: CalculationInputObj) => Observable<CalculationInputObj>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface UserPromiseChain {
  defaultCalculationTemplate: CalculationTemplatePromiseChain & {
    execute: (
      request: CalculationTemplateRequest,
      defaultValue?: CalculationTemplate | null,
    ) => Promise<CalculationTemplate | null>
  }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  language: { execute: (request?: boolean | number, defaultValue?: UserLanguages) => Promise<UserLanguages> }
  country: {
    execute: (request?: boolean | number, defaultValue?: MainUserCountryChoices) => Promise<MainUserCountryChoices>
  }
  clientCustomization: {
    execute: (
      request?: boolean | number,
      defaultValue?: UserClientCustomizations | null,
    ) => Promise<UserClientCustomizations | null>
  }
  calculationTemplates: {
    execute: (request: CalculationTemplateRequest, defaultValue?: CalculationTemplate[]) => Promise<CalculationTemplate[]>
  }
}

export interface UserObservableChain {
  defaultCalculationTemplate: CalculationTemplateObservableChain & {
    execute: (
      request: CalculationTemplateRequest,
      defaultValue?: CalculationTemplate | null,
    ) => Observable<CalculationTemplate | null>
  }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  language: { execute: (request?: boolean | number, defaultValue?: UserLanguages) => Observable<UserLanguages> }
  country: {
    execute: (request?: boolean | number, defaultValue?: MainUserCountryChoices) => Observable<MainUserCountryChoices>
  }
  clientCustomization: {
    execute: (
      request?: boolean | number,
      defaultValue?: UserClientCustomizations | null,
    ) => Observable<UserClientCustomizations | null>
  }
  calculationTemplates: {
    execute: (request: CalculationTemplateRequest, defaultValue?: CalculationTemplate[]) => Observable<CalculationTemplate[]>
  }
}

export interface MutationPromiseChain {
  calculate: (args: {
    input: CalculationInput
  }) => CalculatePromiseChain & { execute: (request: CalculateRequest, defaultValue?: Calculate) => Promise<Calculate> }
  forgotPassword: (args: {
    email: String
    language: UserLanguages
  }) => ForgotPasswordPromiseChain & {
    execute: (request: ForgotPasswordRequest, defaultValue?: ForgotPassword | null) => Promise<ForgotPassword | null>
  }
  login: (args: {
    email: String
    password: String
  }) => LoginPromiseChain & { execute: (request: LoginRequest, defaultValue?: Login | null) => Promise<Login | null> }
  logout: LogoutPromiseChain & { execute: (request: LogoutRequest, defaultValue?: Logout | null) => Promise<Logout | null> }
  register: (args: {
    adisseoContactName: String
    company: String
    country: String
    email: String
    language: UserLanguages
    name: String
    password: String
    password2: String
  }) => RegisterPromiseChain & {
    execute: (request: RegisterRequest, defaultValue?: Register | null) => Promise<Register | null>
  }
  resetPassword: (args: {
    language: UserLanguages
    password: String
    password2: String
    token: UUID
  }) => ResetPasswordPromiseChain & {
    execute: (request: ResetPasswordRequest, defaultValue?: ResetPassword | null) => Promise<ResetPassword | null>
  }
  templateDelete: (args: {
    id: ID
  }) => TemplateDeletePromiseChain & {
    execute: (request: TemplateDeleteRequest, defaultValue?: TemplateDelete | null) => Promise<TemplateDelete | null>
  }
  templateSave: (args: {
    calculationInput: CalculationInput
    name: String
  }) => TemplateSavePromiseChain & {
    execute: (request: TemplateSaveRequest, defaultValue?: TemplateSave | null) => Promise<TemplateSave | null>
  }
  updateCurrentUser: ((args?: {
    defaultCalculationTemplateId?: Int | null
    language?: UserLanguages | null
    name?: String | null
  }) => UpdateCurrentUserPromiseChain & {
    execute: (
      request: UpdateCurrentUserRequest,
      defaultValue?: UpdateCurrentUser | null,
    ) => Promise<UpdateCurrentUser | null>
  }) &
    (UpdateCurrentUserPromiseChain & {
      execute: (
        request: UpdateCurrentUserRequest,
        defaultValue?: UpdateCurrentUser | null,
      ) => Promise<UpdateCurrentUser | null>
    })
  ping: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface MutationObservableChain {
  calculate: (args: {
    input: CalculationInput
  }) => CalculateObservableChain & {
    execute: (request: CalculateRequest, defaultValue?: Calculate) => Observable<Calculate>
  }
  forgotPassword: (args: {
    email: String
    language: UserLanguages
  }) => ForgotPasswordObservableChain & {
    execute: (request: ForgotPasswordRequest, defaultValue?: ForgotPassword | null) => Observable<ForgotPassword | null>
  }
  login: (args: {
    email: String
    password: String
  }) => LoginObservableChain & { execute: (request: LoginRequest, defaultValue?: Login | null) => Observable<Login | null> }
  logout: LogoutObservableChain & {
    execute: (request: LogoutRequest, defaultValue?: Logout | null) => Observable<Logout | null>
  }
  register: (args: {
    adisseoContactName: String
    company: String
    country: String
    email: String
    language: UserLanguages
    name: String
    password: String
    password2: String
  }) => RegisterObservableChain & {
    execute: (request: RegisterRequest, defaultValue?: Register | null) => Observable<Register | null>
  }
  resetPassword: (args: {
    language: UserLanguages
    password: String
    password2: String
    token: UUID
  }) => ResetPasswordObservableChain & {
    execute: (request: ResetPasswordRequest, defaultValue?: ResetPassword | null) => Observable<ResetPassword | null>
  }
  templateDelete: (args: {
    id: ID
  }) => TemplateDeleteObservableChain & {
    execute: (request: TemplateDeleteRequest, defaultValue?: TemplateDelete | null) => Observable<TemplateDelete | null>
  }
  templateSave: (args: {
    calculationInput: CalculationInput
    name: String
  }) => TemplateSaveObservableChain & {
    execute: (request: TemplateSaveRequest, defaultValue?: TemplateSave | null) => Observable<TemplateSave | null>
  }
  updateCurrentUser: ((args?: {
    defaultCalculationTemplateId?: Int | null
    language?: UserLanguages | null
    name?: String | null
  }) => UpdateCurrentUserObservableChain & {
    execute: (
      request: UpdateCurrentUserRequest,
      defaultValue?: UpdateCurrentUser | null,
    ) => Observable<UpdateCurrentUser | null>
  }) &
    (UpdateCurrentUserObservableChain & {
      execute: (
        request: UpdateCurrentUserRequest,
        defaultValue?: UpdateCurrentUser | null,
      ) => Observable<UpdateCurrentUser | null>
    })
  ping: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface CalculatePromiseChain {
  currency: CurrencyPromiseChain & { execute: (request: CurrencyRequest, defaultValue?: Currency) => Promise<Currency> }
  product: ProductPromiseChain & { execute: (request: ProductRequest, defaultValue?: Product) => Promise<Product> }
  productPriceConverted: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  iofcShortAndLongTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  iofcShortTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  roiShortAndLongTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  roiShortTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  weightUnits: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  investment: CalculationsInvestmentPromiseChain & {
    execute: (
      request: CalculationsInvestmentRequest,
      defaultValue?: CalculationsInvestment,
    ) => Promise<CalculationsInvestment>
  }
  shortTermReturns: CalculationsShortTermReturnsPromiseChain & {
    execute: (
      request: CalculationsShortTermReturnsRequest,
      defaultValue?: CalculationsShortTermReturns,
    ) => Promise<CalculationsShortTermReturns>
  }
  longTermReturnsReproduction: CalculationsLongTermReturnsReproductionPromiseChain & {
    execute: (
      request: CalculationsLongTermReturnsReproductionRequest,
      defaultValue?: CalculationsLongTermReturnsReproduction,
    ) => Promise<CalculationsLongTermReturnsReproduction>
  }
  longTermReturnsHealth: CalculationsLongTermReturnsHealthPromiseChain & {
    execute: (
      request: CalculationsLongTermReturnsHealthRequest,
      defaultValue?: CalculationsLongTermReturnsHealth,
    ) => Promise<CalculationsLongTermReturnsHealth>
  }
}

export interface CalculateObservableChain {
  currency: CurrencyObservableChain & {
    execute: (request: CurrencyRequest, defaultValue?: Currency) => Observable<Currency>
  }
  product: ProductObservableChain & { execute: (request: ProductRequest, defaultValue?: Product) => Observable<Product> }
  productPriceConverted: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  iofcShortAndLongTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  iofcShortTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  roiShortAndLongTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  roiShortTerm: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  weightUnits: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  investment: CalculationsInvestmentObservableChain & {
    execute: (
      request: CalculationsInvestmentRequest,
      defaultValue?: CalculationsInvestment,
    ) => Observable<CalculationsInvestment>
  }
  shortTermReturns: CalculationsShortTermReturnsObservableChain & {
    execute: (
      request: CalculationsShortTermReturnsRequest,
      defaultValue?: CalculationsShortTermReturns,
    ) => Observable<CalculationsShortTermReturns>
  }
  longTermReturnsReproduction: CalculationsLongTermReturnsReproductionObservableChain & {
    execute: (
      request: CalculationsLongTermReturnsReproductionRequest,
      defaultValue?: CalculationsLongTermReturnsReproduction,
    ) => Observable<CalculationsLongTermReturnsReproduction>
  }
  longTermReturnsHealth: CalculationsLongTermReturnsHealthObservableChain & {
    execute: (
      request: CalculationsLongTermReturnsHealthRequest,
      defaultValue?: CalculationsLongTermReturnsHealth,
    ) => Observable<CalculationsLongTermReturnsHealth>
  }
}

export interface CalculationsInvestmentPromiseChain {
  cowsAmount: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  productPricePerCowDay: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  dmiWithProduct: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  dmCostWithProduct: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  feedCostPerCowDay: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  feedCostPerCowDayWithProduct: ValuesPerPhasePromiseChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Promise<ValuesPerPhase>
  }
  totalFeedCostPerCowYear: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalFeedCostPerCowYearWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalFeedCostPerCowYearDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalFeedCostDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface CalculationsInvestmentObservableChain {
  cowsAmount: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  productPricePerCowDay: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  dmiWithProduct: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  dmCostWithProduct: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  feedCostPerCowDay: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  feedCostPerCowDayWithProduct: ValuesPerPhaseObservableChain & {
    execute: (request: ValuesPerPhaseRequest, defaultValue?: ValuesPerPhase) => Observable<ValuesPerPhase>
  }
  totalFeedCostPerCowYear: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalFeedCostPerCowYearWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalFeedCostPerCowYearDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalFeedCostDifference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface ValuesPerPhasePromiseChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface ValuesPerPhaseObservableChain {
  farOff: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  closeUp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fresh: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  earlyMidLact: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  lateLact: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface CalculationsShortTermReturnsPromiseChain {
  milkPerCowDayWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fatPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  proteinPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  otherSolidsPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fatPerCowDay: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  proteinPerCowDay: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  otherSolidsPerCowDay: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  ecmPerCowDay: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  impactCwtCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  impactPerCowDayCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  impactPerCowYearCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  totalImpactCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
}

export interface CalculationsShortTermReturnsObservableChain {
  milkPerCowDayWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fatPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  proteinPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  otherSolidsPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fatPerCowDay: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  proteinPerCowDay: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  otherSolidsPerCowDay: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  ecmPerCowDay: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  impactCwtCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  impactPerCowDayCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  impactPerCowYearCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  totalImpactCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
}

export interface DifferencePromiseChain {
  current: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  withProduct: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  difference: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface DifferenceObservableChain {
  current: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  withProduct: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  difference: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface CalculationsLongTermReturnsReproductionPromiseChain {
  rollingHerdAvg: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  pregnancyRateWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  pregnancyLossWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  calfSales: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  mortalityCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  reproductiveCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  reproductiveCullingCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  nonReproductiveCullingCost: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  totalReturns: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  totalReturnsFinal: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
}

export interface CalculationsLongTermReturnsReproductionObservableChain {
  rollingHerdAvg: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  pregnancyRateWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  pregnancyLossWithProduct: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  calfSales: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  mortalityCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  reproductiveCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  reproductiveCullingCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  nonReproductiveCullingCost: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  totalReturns: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  totalReturnsFinal: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
}

export interface CalculationsLongTermReturnsHealthPromiseChain {
  mastitis: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  ketosisClinical: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  ketosisSubclinical: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  metritis: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  totalSavings: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
  totalSavingsFinal: DifferencePromiseChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Promise<Difference>
  }
}

export interface CalculationsLongTermReturnsHealthObservableChain {
  mastitis: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  ketosisClinical: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  ketosisSubclinical: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  metritis: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  totalSavings: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
  totalSavingsFinal: DifferenceObservableChain & {
    execute: (request: DifferenceRequest, defaultValue?: Difference) => Observable<Difference>
  }
}

export interface ForgotPasswordPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface ForgotPasswordObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface LoginPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface LoginObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface LogoutPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface LogoutObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface RegisterPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface RegisterObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface ResetPasswordPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface ResetPasswordObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface TemplateDeletePromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface TemplateDeleteObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface TemplateSavePromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface TemplateSaveObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface UpdateCurrentUserPromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface UpdateCurrentUserObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}
